import { useState } from 'react';
import { ArrowInline } from "@/lib/Icon";
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { Button, Card } from '@/design-system/primitives';
import {
  useFireSubscriptionFill,
  useReleaseSubscriptionFillHold,
  useSubscriptionFills,
} from '@/lib/api/adminHooks';
import type { ScheduledShipmentRow } from '@/lib/api/adminHooks';

/**
 * Subscription fills queue — fills 2..N for multi-shipment plans.
 *
 * When an `invoice.paid` webhook covers a multi-cycle plan whose BUD
 * requires more than one fill, the backend writes N rows here. Fill #1
 * fires inline; the rest are `pending` until manually fired (Phase 1)
 * or auto-fired by a daily cron (Phase 2). Each pending row's
 * `scheduled_for` is the date the fill should ship per the SKU's
 * cadence pattern.
 */
export function SubscriptionFillsScreen() {
  const { data, isLoading, isError, error } = useSubscriptionFills();

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <div className="flex items-baseline justify-between flex-wrap gap-10">
          <h1 className="type-heading-xl">Subscription fills</h1>
          <Link
            to="/admin/clinical-ops"
            className="type-body-sm text-accent hover:underline"
          >
            ← Clinical Ops
          </Link>
        </div>
        <p className="type-body-md text-ink-secondary max-w-prose">
          Multi-shipment plans split into one row per fill. Cycle 1 fires
          when the invoice is paid; later cycles wait here until you
          click <em>Fire now</em> (Phase 2 will auto-fire on the scheduled
          date).
        </p>
      </div>

      {isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading…</p>
      ) : isError ? (
        <p className="type-body-sm text-status-danger">
          Failed to load: {String((error as Error)?.message || error)}
        </p>
      ) : (
        <>
          <Section
            title={`Pending (${data?.pending.length ?? 0})`}
            rows={data?.pending ?? []}
            showFireButton
          />
          <Section
            title={`Submitted (${data?.submitted.length ?? 0})`}
            rows={data?.submitted ?? []}
            showFireButton={false}
          />
        </>
      )}
    </div>
  );
}

function Section({
  title,
  rows,
  showFireButton,
}: {
  title: string;
  rows: ScheduledShipmentRow[];
  showFireButton: boolean;
}) {
  if (rows.length === 0) {
    return (
      <section>
        <h2 className="type-heading-lg mb-10">{title}</h2>
        <p className="type-body-sm text-ink-muted">None.</p>
      </section>
    );
  }
  return (
    <section>
      <h2 className="type-heading-lg mb-10">{title}</h2>
      <div className="flex flex-col gap-12">
        {rows.map((r) => (
          <FillRow key={r.id} row={r} showFireButton={showFireButton} />
        ))}
      </div>
    </section>
  );
}

function FillRow({
  row,
  showFireButton,
}: {
  row: ScheduledShipmentRow;
  showFireButton: boolean;
}) {
  const fire = useFireSubscriptionFill();
  const release = useReleaseSubscriptionFillHold();
  const [localStatus, setLocalStatus] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [releaseId, setReleaseId] = useState(() => crypto.randomUUID());
  const [releaseRecorded, setReleaseRecorded] = useState(false);

  const scheduledLabel = row.scheduled_for
    ? format(parseISO(row.scheduled_for), 'MMM d yyyy')
    : '—';
  const who = row.patient_display_name || row.patient_email || row.patient_id;

  const status = localStatus ?? row.status;
  const partnerName =
    row.partner_key === 'the_pharmacy_hub'
      ? 'The Pharmacy Hub'
      : row.partner_key === 'precisionmeds'
        ? 'Precision'
        : row.partner_key;

  async function handleFire() {
    if (
      !window.confirm(
        `Fire shipment ${row.shipment_index} of ${row.total_shipments} now?\n\n` +
          `Patient: ${who}\n` +
          `Product: ${row.product_id}\n` +
          `Quantity: ${row.container_qty} container${row.container_qty === 1 ? '' : 's'} (${row.total_transmitted_qty} units)\n` +
          `Partner: ${partnerName}\n` +
          `Scheduled: ${scheduledLabel}`
      )
    ) {
      return;
    }
    try {
      const r = await fire.mutateAsync({ fillId: row.id });
      setLocalStatus(r.status);
      if (r.error) setLocalError(r.error);
    } catch (e) {
      setLocalStatus('failed');
      setLocalError(String((e as Error)?.message || e));
    }
  }

  async function handleRelease(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reason = releaseReason.trim();
    if (!reason || releaseRecorded) return;
    setLocalError(null);
    try {
      const result = await release.mutateAsync({
        fillId: row.id,
        idempotencyKey: releaseId,
        reason,
      });
      setLocalStatus(result.status);
      setLocalError(result.error ?? null);
      setReleaseRecorded(result.status !== 'reconciliation_pending');
    } catch (error) {
      // Keep the same idempotency key. Replaying after a lost response is safe
      // and cannot cause a second pharmacy submission.
      setLocalError(String((error as Error)?.message || error));
    }
  }

  function startAnotherRelease() {
    setReleaseId(crypto.randomUUID());
    setReleaseReason('');
    setReleaseRecorded(false);
    setLocalError(null);
    setLocalStatus(null);
  }

  return (
    <Card padding="lg">
      <Card.Body>
        <div className="flex items-start justify-between gap-15 flex-wrap mb-12">
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <h3 className="type-title-md truncate">
              {who} — fill {row.shipment_index} of {row.total_shipments}
            </h3>
            <div className="type-label-sm text-ink-muted truncate">
              {row.product_id} · {partnerName}
            </div>
          </div>
          {showFireButton && status === 'pending' && !row.fulfillment_hold && (
            <Button
              size="sm"
              onClick={handleFire}
              loading={fire.isPending}
              disabled={status !== 'pending'}
            >
              Fire now <ArrowInline />
            </Button>
          )}
          {status === 'submitted' && (
            <span className=" bg-status-success-subtle text-status-success type-label-sm uppercase tracking-tight px-10 py-4">
              Submitted ✓
            </span>
          )}
          {status === 'failed' && (
            <span className=" bg-status-danger-subtle text-status-danger type-label-sm uppercase tracking-tight px-10 py-4">
              Failed
            </span>
          )}
          {status === 'reconciliation_pending' && (
            <span className="bg-status-warning-subtle text-status-warning type-label-sm uppercase tracking-tight px-10 py-4">
              Reconciliation pending
            </span>
          )}
          {(row.fulfillment_hold || status === 'held_for_review') && (
            <span className="bg-status-warning-subtle text-status-warning type-label-sm uppercase tracking-tight px-10 py-4">
              Held for review
            </span>
          )}
        </div>

        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-20 gap-y-10">
          <Field label="Cycle" value={`${row.cycle_index}`} />
          <Field
            label="Containers"
            value={`${row.container_qty} (${row.total_transmitted_qty} units)`}
          />
          <Field label="Scheduled for" value={scheduledLabel} />
          <Field label="Invoice" value={row.stripe_invoice_id} />
          <Field
            label="Pharmacy order"
            value={row.pharmacy_order_id ?? '—'}
          />
          <Field
            label="Subscription"
            value={row.stripe_subscription_id || '—'}
          />
          {row.blocking_stripe_invoice_id && (
            <Field
              label="Blocking invoice"
              value={row.blocking_stripe_invoice_id}
            />
          )}
        </dl>

        {row.fulfillment_hold && status !== 'submitted' && (
          <form
            className="mt-15 flex flex-col gap-10 border-t border-line-subtle pt-15"
            onSubmit={handleRelease}
          >
            <div>
              <div className="type-title-sm">Explicit hold release</div>
              <p className="type-body-sm text-ink-muted mt-4">
                This authorizes one checked attempt. Billing, invoice-frontier,
                clinical, routing, and pharmacy checks run again. A failed
                attempt stays held and never retries automatically.
              </p>
            </div>
            <label className="flex flex-col gap-4 type-label-sm" htmlFor={`release-${row.id}`}>
              Release reason
              <textarea
                id={`release-${row.id}`}
                value={releaseReason}
                onChange={(event) => setReleaseReason(event.target.value)}
                rows={2}
                disabled={releaseRecorded || release.isPending}
                className="w-full border border-line bg-raised px-10 py-8 type-body-sm"
                placeholder="What was reviewed and why this exact fill is safe to retry"
              />
            </label>
            <div className="flex flex-wrap items-center gap-10">
              <Button
                size="sm"
                type="submit"
                loading={release.isPending}
                disabled={!releaseReason.trim() || releaseRecorded}
              >
                {status === 'reconciliation_pending'
                  ? 'Retry reconciliation with same release ID'
                  : releaseRecorded
                    ? 'Release attempt recorded'
                    : 'Release hold and retry'}
              </Button>
              {releaseRecorded && status !== 'submitted' && (
                <Button size="sm" type="button" variant="secondary" onClick={startAnotherRelease}>
                  Start another reviewed attempt
                </Button>
              )}
            </div>
          </form>
        )}

        {(localError || row.failure_reason) && (
          <div
            role="alert"
            className="mt-12 bg-status-danger-subtle text-status-danger px-15 py-10 type-body-sm"
          >
            {localError || row.failure_reason}
          </div>
        )}
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {release.isPending
            ? 'Release checks in progress.'
            : fire.isPending
              ? 'Shipment checks in progress.'
              : status === 'reconciliation_pending'
                ? 'The provider accepted the fill. Retry reconciliation with the same release ID; no order will be resent.'
                : releaseRecorded
                ? status === 'submitted'
                  ? 'Release succeeded and the fill was submitted.'
                  : 'Release attempt completed without submission. The fill remains held.'
                : localError
                  ? `Action failed: ${localError}`
                  : ''}
        </p>
      </Card.Body>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="type-label-sm text-ink-muted uppercase tracking-tight">
        {label}
      </dt>
      <dd className="type-body-md break-words">{value}</dd>
    </div>
  );
}
